import { autocompletion, Completion, CompletionContext } from '@codemirror/autocomplete'
import { AddRounded, DeleteRounded, InfoRounded } from '@mui/icons-material'
import { Box, Button, Divider, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material'
import { ChangeEvent, memo, ReactNode, useEffect } from 'react'
import { IFlow, INode } from '../../../../definitions/Nodes'
import { IAcceptedData } from '../../../../definitions/NodesDescription'
import NodeManager from '../../../../managers/NodeManager'
import NodeCodemirrorTextEditor from '../NodeCodemirrorTextEditor'

interface P {
    acceptedData: IAcceptedData<'xfrm_caseWhen'>
    selectedNode: INode<'xfrm_caseWhen'>
    flow: IFlow
    setSelected: (node: INode<'xfrm_caseWhen'>) => void
    disable: boolean
}

const renderInfoField = (title: string | ReactNode, disable: boolean) => (
    <Tooltip arrow title={title} disableHoverListener={disable}>
        <InfoRounded fontSize='inherit' sx={{ cursor: disable ? undefined : 'pointer', color: disable ? 'var(--icon-disabled)' : 'var(--icon-color)' }} />
    </Tooltip>
)

const NodeCaseWhenEditor = ({ selectedNode, setSelected, disable, flow }: P) => {
    useEffect(() => {
        if (!selectedNode.data?.caseWhenData) {
            setSelected({
                ...selectedNode,
                data: {
                    ...selectedNode.data,
                    caseWhenData: {
                        cases: [{ then: '', when: '' }],
                        else: '',
                        output: ''
                    }
                }
            })
        }
    }, [])

    const caseWhenDataShape = NodeManager.getMappedShapes(NodeManager.getInputShapes<'xfrm_caseWhen'>(selectedNode, flow.nodes).find(s => s.argKey === 'data'))
    const globalsDataShape = flow?.globals ? Object.keys(flow.globals) : []

    const inputDataSuggestions: Completion[] = caseWhenDataShape.length > 0
        ? caseWhenDataShape.map(x => ({
            label: x,
            type: 'field',
            info: '-> Field',
            apply: `@{${x}}`
        }))
        : []

    const globalsSuggestions: Completion[] = globalsDataShape.length > 0
        ? globalsDataShape.map(x => ({
            label: `globals.${x}`,
            type: 'globals',
            info: '-> Globals Input',
            apply: `@{globals.${x}}`
        }))
        : []

    const myCompletions = (context: CompletionContext) => {
        const word = context.matchBefore(/\w*/)
        if (word.from === word.to && !context.explicit) return null
        return {
            from: word.from,
            options: [...inputDataSuggestions, ...globalsSuggestions]
        }
    }

    const handleAddCase = () => {
        const currentCases = selectedNode.data?.caseWhenData?.cases ?? []
        setSelected({
            ...selectedNode,
            data: {
                ...selectedNode.data,
                caseWhenData: {
                    ...selectedNode.data?.caseWhenData,
                    cases: [...currentCases, { when: '', then: '' }]
                }
            }
        })
    }

    const handleDeleteCase = (index: number) => {
        const currentCases = selectedNode.data?.caseWhenData?.cases ?? []
        const newCases = currentCases.filter((_, i) => i !== index)
        setSelected({
            ...selectedNode,
            data: {
                ...selectedNode.data,
                caseWhenData: {
                    ...selectedNode.data?.caseWhenData,
                    cases: newCases
                }
            }
        })
    }

    const handleWhenThen = (value: string, index: number, field: 'when' | 'then') => {
        const newCases = [...(selectedNode.data?.caseWhenData?.cases ?? [])]
        newCases[index] = { ...newCases[index], [field]: value }
        setSelected({
            ...selectedNode,
            data: {
                ...selectedNode.data,
                caseWhenData: {
                    ...selectedNode.data?.caseWhenData,
                    cases: newCases
                }
            }
        })
    }

    const handleOutputElse = (event: string | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field: 'output' | 'else') => {
        setSelected({
            ...selectedNode,
            data: {
                ...selectedNode.data,
                caseWhenData: {
                    ...selectedNode.data?.caseWhenData,
                    [field]: typeof event === 'string' ? event : event.target.value
                }
            }
        })
    }

    return (
        <Stack spacing={3}>
            <Stack spacing={1}>
                <Stack flexDirection='row' gap={1} alignItems='center'>
                    {renderInfoField('Insert the name of output column', disable)}
                    <Typography variant='subtitle2'>New column name:</Typography>
                </Stack>
                <TextField
                    size='small'
                    value={selectedNode.data?.caseWhenData?.output ?? ''}
                    disabled={disable}
                    onChange={e => handleOutputElse(e, 'output')}
                />
            </Stack>
            <Stack spacing={1.5}>
                {selectedNode.data?.caseWhenData?.cases.map((c, i) => (
                    <Stack key={i} flexDirection='row' gap={1.5} alignItems='center'>
                        <Stack flexDirection='row' gap={1} alignItems='center'>
                            {renderInfoField('Insert the condition', disable)}
                            <Typography variant='body2'>When</Typography>
                        </Stack>
                        <NodeCodemirrorTextEditor
                            value={c.when}
                            disabled={disable}
                            onChange={val => handleWhenThen(val, i, 'when')}
                            extensions={[autocompletion({ override: [myCompletions] })]}
                        />
                        <Stack flexDirection='row' gap={1} alignItems='center'>
                            {renderInfoField('Insert the operation', disable)}
                            <Typography variant='body2'>Then</Typography>
                        </Stack>
                        <NodeCodemirrorTextEditor
                            value={c.then}
                            disabled={disable}
                            onChange={val => handleWhenThen(val, i, 'then')}
                            extensions={[autocompletion({ override: [myCompletions] })]}
                        />
                        <Box>
                            <IconButton
                                size='small'
                                color='error'
                                onClick={() => handleDeleteCase(i)}
                                disabled={disable}>
                                <DeleteRounded fontSize='small' />
                            </IconButton>
                        </Box>
                    </Stack>
                ))}

                <Button
                    size='small'
                    variant='text'
                    startIcon={<AddRounded fontSize='small' />}
                    onClick={handleAddCase}
                    disabled={disable}>
                    add case
                </Button>
            </Stack>
            <Divider />
            <Stack flexDirection='row' gap={1.5} alignItems='center'>
                <Stack flexDirection='row' gap={1} alignItems='center'>
                    {renderInfoField('Insert the else condition', disable)}
                    <Typography variant='body2'>Else</Typography>
                </Stack>
                <NodeCodemirrorTextEditor
                    value={selectedNode.data?.caseWhenData?.else ?? ''}
                    disabled={disable}
                    onChange={e => handleOutputElse(e, 'else')}
                    extensions={[autocompletion({ override: [myCompletions] })]}
                />
            </Stack>
        </Stack>
    )
}

export default memo(NodeCaseWhenEditor)
